/**
 * @fileoverview The Control Panel's search affordance.
 *
 * NOT AN INPUT, AND THAT IS THE POINT. Ours was a Blueprint `InputGroup` — a
 * real text field you could type into, which then navigated away on focus. The
 * capture has a `div[tabindex=0]` that opens the settings dialog: it looks like
 * a field and behaves like a button, because the searching happens somewhere
 * else. Typing into a control that discards what you type is worse than a
 * control that never accepted it.
 *
 * Structure and classes from the recovered component
 * (../palantir/…/foundry-apps/control-panel/src/components/SearchBar.tsx) and
 * confirmed against the captured tenant home. All five parts are in our
 * vendored map:
 *
 *   control-panel-internal-components__search-bar__c9tkov          [tabindex=0]
 *     …__search-bar-icon-and-placeholder__c9tkov
 *       span.bp6-icon-search …__search-bar-icon__c9tkov
 *       div …__search-bar-text__c9tkov
 *     …__search-bar-shortcut-text__c9tkov > span.bp6-key-combo.bp6-minimal
 */

import { Icon, KeyComboTag } from "@blueprintjs/core";

import { controlPanelInternalComponents as cpi } from "../../../../components/control-panel/foundry-parts.generated";

export interface SearchBarProps {
  /** Opened by click, Enter or Space. */
  readonly onOpen: () => void;
  readonly placeholder?: string;
}

export function SearchBar({
  onOpen,
  placeholder = "What setting are you looking for?",
}: SearchBarProps): React.ReactElement {
  return (
    <div
      className={cpi["search-bar"]}
      data-testid="control-panel-search"
      /*
        `button` and a tabindex, because it is one. Without the role a screen
        reader announces a focusable group with no purpose; the visual
        resemblance to a text field is styling, not semantics, and the two
        should not agree here.
      */
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          // Space scrolls the page otherwise, which is the wrong response to
          // activating a control.
          event.preventDefault();
          onOpen();
        }
      }}
    >
      <div className={cpi["search-bar-icon-and-placeholder"]}>
        <Icon icon="search" className={cpi["search-bar-icon"]} />
        <div className={cpi["search-bar-text"]}>{placeholder}</div>
      </div>
      <div className={cpi["search-bar-shortcut-text"]}>
        <KeyComboTag combo="ctrl + J" minimal />
      </div>
    </div>
  );
}
